import clsxm from '@/lib/clsxm';
import { LotterySet, lotterySpecialLabel } from '@/lib/lottery';

type LotteryBallsProps = {
  sets: LotterySet[];
  preset?: string | null;
};

function Ball({
  value,
  variant = 'main',
}: {
  value: number;
  variant?: 'main' | 'powerball' | 'megaball';
}) {
  return (
    <span
      className={clsxm(
        'inline-flex h-11 w-11 select-none items-center justify-center rounded-full text-base font-bold shadow-md ring-1 sm:h-12 sm:w-12 sm:text-lg',
        variant === 'powerball' &&
          'bg-gradient-to-b from-red-500 to-red-600 text-white ring-red-700/30',
        variant === 'megaball' &&
          'bg-gradient-to-b from-amber-400 to-amber-500 text-amber-950 ring-amber-600/40',
        variant === 'main' &&
          'bg-gradient-to-b from-white to-gray-100 text-gray-800 ring-gray-300 dark:from-slate-100 dark:to-slate-200 dark:text-gray-900',
      )}
    >
      {value}
    </span>
  );
}

export default function LotteryBalls({ sets, preset }: LotteryBallsProps) {
  if (!sets || sets.length === 0) return null;

  const specialLabel = lotterySpecialLabel(preset);
  const specialVariant =
    preset === 'mega-millions-number-generator' ? 'megaball' : 'powerball';

  return (
    <div className='space-y-4'>
      {sets.map((set, i) => (
        <div
          key={i}
          className='rounded-xl border border-gray-100 bg-gray-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/40'
        >
          <div className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500'>
            {sets.length > 1 ? `Set ${i + 1}` : 'Your numbers'}
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            {set.main.map((n, j) => (
              <Ball key={j} value={n} />
            ))}
            {set.specials && set.specials.length > 0 && (
              <>
                <span
                  aria-hidden
                  className='mx-0.5 text-xl font-light text-gray-300 dark:text-slate-600'
                >
                  +
                </span>
                {set.specials.map((special, j) => (
                  <span key={j} className='inline-flex flex-col items-center'>
                    <Ball value={special} variant={specialVariant} />
                    {specialLabel && (
                      <span className='mt-1 text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500'>
                        {specialLabel}
                      </span>
                    )}
                  </span>
                ))}
              </>
            )}
          </div>
          {set.analysis && (
            <p className='mt-3 text-sm text-gray-600 dark:text-slate-400'>
              {set.analysis}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
